'use client';

import { SERVICE, useDeviceInfo } from '@musinsa/packages-device-detection';
import type { CouponGoodsItemDto } from '@musinsa/services-curator';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { FloatingButtonGroup, ScrollToTopButton } from '../../common/components';
import { useCouponLinkCopy, useNavigate } from '../../common/hooks';
import { pathsMusinsa } from '../../common/paths/musinsa';
import { curatorQueries } from '../../query-factory';
import {
  buildCouponGoodsTitleImpressionGtmAttr,
  SCREEN_COUPON_GOODS_OWNER,
  trackCouponGoodsCopyLinkClickMusinsa,
  trackCouponGoodsCuratorShopClickMusinsa,
  trackCouponGoodsProductImpressionMusinsa,
  trackOwnerCouponGoodsProductClickMusinsa,
  trackOwnerCouponGoodsScreenViewMusinsa,
} from '../utils/couponGoodsMusinsaAnalytics';
import { CouponGoodsList } from './CouponGoodsList';
import { OwnerCouponHeaderCard } from './OwnerCouponHeaderCard';

interface OwnerCouponGoodsPageProps {
  shopSlug: string;
  couponNo: number;
  className?: string;
}

export const OwnerCouponGoodsPage = ({
  shopSlug,
  couponNo,
  className,
}: OwnerCouponGoodsPageProps) => {
  const { service } = useDeviceInfo();
  const isMusinsa = service === SERVICE.MUSINSA;
  const { navigate } = useNavigate();

  const { data } = useQuery(curatorQueries.coupon.goods({ couponNo }));

  // NOTE: webview Safari 정책 회피 위해 사전 fetch 한 short link 를 click 시 동기 복사.
  const copyLink = useCouponLinkCopy({ couponNo });

  const couponType = data ? String(data.salesRate) : '';

  useEffect(() => {
    if (!isMusinsa || !data) return;
    trackOwnerCouponGoodsScreenViewMusinsa({
      brandId: data.brandId,
      couponId: couponNo,
      couponName: data.couponName,
      couponType,
      curatorId: shopSlug,
      fromScreen: SCREEN_COUPON_GOODS_OWNER,
    });
  }, [isMusinsa, data, couponNo, shopSlug, couponType]);

  const handleClickShop = useCallback(() => {
    if (isMusinsa && data) {
      trackCouponGoodsCuratorShopClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_OWNER,
      });
    }
    navigate(pathsMusinsa.myShop({ snsTitle: shopSlug }));
  }, [isMusinsa, data, navigate, shopSlug, couponNo, couponType]);

  const handleCopyLink = useCallback(() => {
    if (isMusinsa && data) {
      trackCouponGoodsCopyLinkClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_OWNER,
      });
    }
    copyLink();
  }, [isMusinsa, data, copyLink, shopSlug, couponNo, couponType]);

  const handleProductImpression = useCallback(
    (item: CouponGoodsItemDto) => {
      if (!data) return;
      trackCouponGoodsProductImpressionMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_OWNER,
        productId: item.goodsNo,
      });
    },
    [data, couponNo, shopSlug, couponType],
  );

  const handleClickProduct = useCallback(
    (item: CouponGoodsItemDto) => {
      if (!data) return;
      trackOwnerCouponGoodsProductClickMusinsa({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_OWNER,
        productId: item.goodsNo,
        productName: item.goodsName,
      });
    },
    [data, couponNo, shopSlug, couponType],
  );

  if (!data) return null;

  // GA4 impression_content (쿠폰명 타이틀 노출) — 헤더 카드 컨테이너에 className + data-* 부여.
  // className 만 박으면 GTM handler 가 element.getAttribute('data-xxx') 호출 시 null 반환.
  const titleGtm = isMusinsa
    ? buildCouponGoodsTitleImpressionGtmAttr({
        brandId: data.brandId,
        couponId: couponNo,
        couponName: data.couponName,
        couponType,
        curatorId: shopSlug,
        fromScreen: SCREEN_COUPON_GOODS_OWNER,
      })
    : null;
  const { className: titleGtmClassName, ...titleGtmDataAttrs } = titleGtm ?? {
    className: undefined,
  };

  return (
    <div className={twMerge('flex grow flex-col bg-gray-100 safe-area-bottom', className)}>
      <div {...titleGtmDataAttrs} className={titleGtmClassName}>
        <OwnerCouponHeaderCard
          data={data}
          onClickShop={handleClickShop}
          onCopyLink={handleCopyLink}
        />
      </div>
      <CouponGoodsList
        couponNo={couponNo}
        isOwner={true}
        coupon={{ couponId: couponNo, couponName: data.couponName }}
        onProductImpression={isMusinsa ? handleProductImpression : undefined}
        onClickProduct={isMusinsa ? handleClickProduct : undefined}
      />
      <FloatingButtonGroup.Layout className="flex-col items-end safe-area-bottom">
        <FloatingButtonGroup.Container>
          <ScrollToTopButton />
        </FloatingButtonGroup.Container>
      </FloatingButtonGroup.Layout>
    </div>
  );
};
